import { Box, Container, Typography, Grid } from "@mui/material";
import { useDocumentTitle } from "../../utils/data";
import { useNavigate } from "react-router-dom";
import { keyframes } from "@mui/system";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const floatBounce = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const spinSlow = keyframes`
  from { transform: translate(-50%, -50%) rotate(0deg); }
  to { transform: translate(-50%, -50%) rotate(360deg); }
`;

const industriesData = [
  {
    title: "Retail",
    description: "Transform customer experiences with AI-powered personalization, intelligent inventory management, and seamless omnichannel strategies that drive measurable revenue growth.",
    emoji: "🛍️",
    path: "/industries/retail",
    gradient: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
    shadowColor: "rgba(245, 158, 11, 0.4)",
    highlights: ["AI Personalization", "Smart Inventory", "Omnichannel CX"],
  },
  {
    title: "Healthcare",
    description: "Streamline patient care, enhance diagnostics, and ensure full HIPAA compliance with intelligent, secure healthcare technology solutions built for the modern clinic.",
    emoji: "🏥",
    path: "/industries/healthcare",
    gradient: "linear-gradient(135deg, #10b981 0%, #0891b2 100%)",
    shadowColor: "rgba(16, 185, 129, 0.4)",
    highlights: ["HIPAA Compliance", "Telemedicine", "Patient Analytics"],
  },
  {
    title: "Information Services",
    description: "Secure data management, advanced analytics platforms, and scalable cloud infrastructure for modern information-driven enterprises that need to move fast and stay safe.",
    emoji: "💻",
    path: "/industries/information-services",
    gradient: "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
    shadowColor: "rgba(59, 130, 246, 0.4)",
    highlights: ["Data Security", "Cloud Infrastructure", "Advanced Analytics"],
  },
  {
    title: "Education Sector",
    description: "Enhance learning outcomes with modern LMS platforms, virtual classrooms, adaptive learning systems, and powerful student analytics that keep educators and learners ahead.",
    emoji: "🎓",
    path: "/industries/education",
    gradient: "linear-gradient(135deg, #8b5cf6 0%, #d946ef 100%)",
    shadowColor: "rgba(139, 92, 246, 0.4)",
    highlights: ["LMS Platforms", "Virtual Classrooms", "Student Analytics"],
  },
  {
    title: "Telecom",
    description: "Optimize network operations, elevate customer engagement, and monetize next-gen 5G networks with intelligent telecom platforms designed for the demands of tomorrow.",
    emoji: "📡",
    path: "/industries/telecom",
    gradient: "linear-gradient(135deg, #f43f5e 0%, #f97316 100%)",
    shadowColor: "rgba(244, 63, 94, 0.4)",
    highlights: ["5G Solutions", "Network Optimization", "CX Automation"],
  },
  {
    title: "Finance",
    description: "Secure transactions, AI-driven fraud detection, end-to-end regulatory compliance, and intelligent financial platforms that give modern institutions a decisive competitive edge.",
    emoji: "💰",
    path: "/industries/finance",
    gradient: "linear-gradient(135deg, #059669 0%, #0d9488 100%)",
    shadowColor: "rgba(5, 150, 105, 0.4)",
    highlights: ["Fraud Detection", "RegTech", "FinTech Platforms"],
  },
];

const statsData = [
  { number: "50+", label: "Projects Delivered" },
  { number: "6+", label: "Industries Served" },
  { number: "98%", label: "Client Satisfaction" },
  { number: "20+", label: "Years of Expertise" },
];

const approachData = [
  {
    step: "01",
    title: "Industry Deep Dive",
    description: "A thorough analysis of your industry landscape, regulatory environment, pain points, and untapped opportunities — building the strategic foundation for everything that follows.",
    gradient: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
  },
  {
    step: "02",
    title: "Tailored Solution Design",
    description: "Solutions specifically architected for your sector's unique workflows, compliance requirements, and competitive dynamics — not off-the-shelf, but built for you.",
    gradient: "linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)",
  },
  {
    step: "03",
    title: "Agile Delivery",
    description: "Precision execution using agile methodologies — rapid time-to-value, continuous iterations, and complete transparency every step of the way.",
    gradient: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
  },
  {
    step: "04",
    title: "Sustained Growth",
    description: "Post-launch optimization, intelligent scaling support, and ongoing strategic guidance to ensure your solution grows as fast as your business does.",
    gradient: "linear-gradient(135deg, #10b981 0%, #3b82f6 100%)",
  },
];

const IndustriesIndex = () => {
  useDocumentTitle("Briozon - Industries");
  const navigate = useNavigate();

  return (
    <>
      {/* ─── Hero Banner ─── */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #f8fafc 0%, #f0f4ff 100%)",
          py: { xs: 6, sm: 8, md: 10 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            right: "5%",
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(91,124,250,0.1) 0%, rgba(118,75,162,0.1) 100%)",
            animation: `${floatBounce} 8s ease-in-out infinite`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "15%",
            left: "3%",
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(118,75,162,0.1) 0%, rgba(91,124,250,0.1) 100%)",
            animation: `${floatBounce} 6s ease-in-out infinite 1s`,
          }}
        />

        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, sm: 6, md: 8 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    fontSize: { xs: "12px", sm: "14px" },
                    fontWeight: 700,
                    color: "#5b7cfa",
                    letterSpacing: "2px",
                    mb: 1,
                    display: "block",
                    animation: `${fadeUp} 0.6s ease-out 0.05s both`,
                  }}
                >
                  INDUSTRIES
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: "32px", sm: "42px", md: "48px" },
                    fontWeight: 800,
                    color: "#1b1f33",
                    lineHeight: 1.2,
                    mb: 2,
                    animation: `${fadeUp} 0.6s ease-out 0.1s both`,
                  }}
                >
                  Expertise Across{" "}
                  <Box
                    component="span"
                    sx={{
                      background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    Diverse Industries
                  </Box>
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: "16px", sm: "19px", md: "20px" },
                    color: "#4c5472",
                    lineHeight: 1.8,
                    mb: 3,
                    animation: `${fadeUp} 0.6s ease-out 0.2s both`,
                  }}
                >
                  We are a team of professionals with expertise in various industries,
                  specializing in software development. We focus on delivering innovative
                  solutions and providing top-tier talent to meet your technology needs.
                </Typography>
                <Box
                  component="button"
                  onClick={() => navigate("/contact")}
                  sx={{
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    color: "#fff",
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    borderRadius: "12px",
                    fontSize: "16px",
                    border: "none",
                    cursor: "pointer",
                    boxShadow: "0 8px 24px rgba(91, 124, 250, 0.3)",
                    animation: `${fadeUp} 0.6s ease-out 0.3s both`,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 32px rgba(91, 124, 250, 0.4)",
                    },
                  }}
                >
                  Get in Touch
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  height: { xs: 320, sm: 400, md: 450 },
                  animation: `${fadeUp} 0.8s ease-out 0.2s both`,
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 220, sm: 280, md: 320 },
                    height: { xs: 220, sm: 280, md: 320 },
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, rgba(91,124,250,0.15) 0%, rgba(118,75,162,0.15) 100%)",
                    filter: "blur(40px)",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 100, sm: 120 },
                    height: { xs: 100, sm: 120 },
                    borderRadius: "24px",
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: { xs: "48px", sm: "56px" },
                    boxShadow: "0 20px 40px rgba(91, 124, 250, 0.4)",
                    animation: `${floatBounce} 6s ease-in-out infinite`,
                    zIndex: 2,
                  }}
                >
                  🏢
                </Box>
                {[
                  { top: "5%", left: "5%", emoji: "🛍️", delay: "0.5s", dur: "5s" },
                  { top: "5%", right: "8%", emoji: "🏥", delay: "1s", dur: "6s" },
                  { bottom: "10%", left: "15%", emoji: "💰", delay: "1.5s", dur: "7s" },
                  { bottom: "8%", right: "10%", emoji: "🎓", delay: "2s", dur: "5.5s" },
                ].map((item, i) => (
                  <Box
                    key={i}
                    sx={{
                      position: "absolute",
                      top: item.top,
                      left: item.left,
                      right: item.right,
                      bottom: item.bottom,
                      width: 56,
                      height: 56,
                      borderRadius: "16px",
                      background: "rgba(255,255,255,0.95)",
                      boxShadow: "0 8px 32px rgba(17, 24, 39, 0.1)",
                      backdropFilter: "blur(10px)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "28px",
                      animation: `${floatBounce} ${item.dur} ease-in-out infinite ${item.delay}`,
                      zIndex: 1,
                    }}
                  >
                    {item.emoji}
                  </Box>
                ))}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 200, sm: 260 },
                    height: { xs: 200, sm: 260 },
                    borderRadius: "50%",
                    border: "2px dashed rgba(91, 124, 250, 0.2)",
                    animation: `${floatBounce} 10s ease-in-out infinite`,
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 280, sm: 360 },
                    height: { xs: 280, sm: 360 },
                    borderRadius: "50%",
                    border: "1px dashed rgba(118, 75, 162, 0.15)",
                    animation: `${floatBounce} 12s ease-in-out infinite reverse`,
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ─── Section 1: Industries Showcase ─── */}
      <Box
        sx={{
          py: { xs: 8, sm: 10, md: 14 },
          background: "linear-gradient(180deg, #f0f4ff 0%, #e8eeff 50%, #f4f0ff 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Ambient glows */}
        <Box
          sx={{
            position: "absolute",
            top: "15%",
            left: "-10%",
            width: { xs: 300, md: 600 },
            height: { xs: 300, md: 600 },
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(91,124,250,0.07) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "5%",
            right: "-8%",
            width: { xs: 250, md: 500 },
            height: { xs: 250, md: 500 },
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(118,75,162,0.07) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        {/* Spinning ring */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: { xs: 500, md: 900 },
            height: { xs: 500, md: 900 },
            borderRadius: "50%",
            border: "1px dashed rgba(91,124,250,0.06)",
            animation: `${spinSlow} 60s linear infinite`,
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: { xs: 6, sm: 8, md: 10 } }}>
            <Box
              sx={{
                display: "inline-block",
                px: 2.5,
                py: 0.8,
                mb: 3,
                borderRadius: "50px",
                background: "rgba(91,124,250,0.12)",
                border: "1px solid rgba(91,124,250,0.25)",
                animation: `${fadeUp} 0.6s ease-out both`,
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "11px", sm: "12px" },
                  fontWeight: 700,
                  color: "#5b7cfa",
                  letterSpacing: "3px",
                  textTransform: "uppercase",
                }}
              >
                Industries We Serve
              </Typography>
            </Box>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "30px", sm: "42px", md: "56px" },
                fontWeight: 900,
                color: "#1b1f33",
                lineHeight: 1.1,
                mb: 2.5,
                animation: `${fadeUp} 0.6s ease-out 0.1s both`,
              }}
            >
              Built for Your{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Industry
              </Box>
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "16px", sm: "18px" },
                color: "#4c5472",
                maxWidth: "560px",
                mx: "auto",
                lineHeight: 1.8,
                animation: `${fadeUp} 0.6s ease-out 0.2s both`,
              }}
            >
              Specialized expertise across six major verticals — delivering solutions
              that speak your industry's language and accelerate your growth.
            </Typography>
          </Box>

          {/* Industry Cards */}
          <Grid container spacing={{ xs: 2.5, sm: 3 }}>
            {industriesData.map((industry, idx) => (
              <Grid item xs={12} sm={6} md={4} key={industry.title}>
                <Box
                  sx={{
                    height: { xs: "auto", md: "300px" },
                    minHeight: { xs: "260px" },
                    background: industry.gradient,
                    borderRadius: "24px",
                    p: { xs: 3, sm: 3.5 },
                    position: "relative",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    boxShadow: `0 16px 48px ${industry.shadowColor}`,
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    animation: `${fadeIn} 0.6s ease-out ${0.08 * idx}s both`,
                    // Radial highlight overlay
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: "radial-gradient(circle at 75% 15%, rgba(255,255,255,0.2) 0%, transparent 55%)",
                      pointerEvents: "none",
                    },
                    // Bottom dark gradient
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      inset: 0,
                      background: "linear-gradient(180deg, transparent 45%, rgba(0,0,0,0.22) 100%)",
                      pointerEvents: "none",
                    },
                    "&:hover .arrow-btn": {
                      background: "rgba(255,255,255,0.4)",
                    },
                  }}
                >
                  {/* Top row */}
                  <Box sx={{ position: "relative", zIndex: 1 }}>
                    <Box sx={{ fontSize: { xs: "52px", sm: "58px" }, lineHeight: 1 }}>
                      {industry.emoji}
                    </Box>
                  </Box>

                  {/* Bottom content */}
                  <Box sx={{ position: "relative", zIndex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: { xs: "20px", sm: "22px", md: "23px" },
                        fontWeight: 800,
                        color: "#fff",
                        mb: 0.75,
                        textShadow: "0 2px 12px rgba(0,0,0,0.15)",
                        letterSpacing: "-0.3px",
                      }}
                    >
                      {industry.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: "13px", sm: "14px" },
                        color: "rgba(255,255,255,0.8)",
                        lineHeight: 1.6,
                        mb: 2,
                      }}
                    >
                      {industry.description}
                    </Typography>
                    {/* Highlight chips */}
                    <Box sx={{ display: "flex", gap: 0.8, flexWrap: "wrap" }}>
                      {industry.highlights.map((h) => (
                        <Box
                          key={h}
                          sx={{
                            px: 1.4,
                            py: 0.35,
                            borderRadius: "50px",
                            background: "rgba(255,255,255,0.18)",
                            backdropFilter: "blur(6px)",
                            fontSize: { xs: "11px", sm: "12px" },
                            fontWeight: 600,
                            color: "#fff",
                            border: "1px solid rgba(255,255,255,0.28)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {h}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ─── Section 2: Stats Strip ─── */}
      <Box sx={{ py: { xs: 7, sm: 9 }, background: "#ffffff" }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
              gap: { xs: 5, sm: 6 },
              textAlign: "center",
            }}
          >
            {statsData.map((stat, idx) => (
              <Box
                key={stat.label}
                sx={{
                  position: "relative",
                  animation: `${fadeUp} 0.6s ease-out ${idx * 0.1}s both`,
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    right: 0,
                    top: "15%",
                    width: "1px",
                    height: "70%",
                    background: "linear-gradient(180deg, transparent, rgba(91,124,250,0.18), transparent)",
                    display: { xs: idx % 2 === 0 ? "block" : "none", md: idx < 3 ? "block" : "none" },
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: "40px", sm: "50px", md: "58px" },
                    fontWeight: 900,
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    lineHeight: 1,
                    letterSpacing: "-1px",
                  }}
                >
                  {stat.number}
                </Typography>
                <Typography
                  sx={{
                    mt: 1.2,
                    fontSize: { xs: "14px", sm: "15px" },
                    color: "#6b7280",
                    fontWeight: 500,
                    letterSpacing: "0.3px",
                  }}
                >
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ─── Section 3: Our Approach ─── */}
      <Box
        sx={{
          py: { xs: 8, sm: 10, md: 14 },
          background: "linear-gradient(135deg, #f6f8ff 0%, #f0ebff 60%, #f6f8ff 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Soft background blob */}
        <Box
          sx={{
            position: "absolute",
            top: "40%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: 400, md: 800 },
            height: { xs: 400, md: 800 },
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(91,124,250,0.06) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: { xs: 6, sm: 8 } }}>
            <Box
              sx={{
                display: "inline-block",
                px: 2.5,
                py: 0.8,
                mb: 3,
                borderRadius: "50px",
                background: "rgba(91,124,250,0.08)",
                border: "1px solid rgba(91,124,250,0.2)",
                animation: `${fadeUp} 0.6s ease-out both`,
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "11px", sm: "12px" },
                  fontWeight: 700,
                  color: "#5b7cfa",
                  letterSpacing: "3px",
                  textTransform: "uppercase",
                }}
              >
                How We Work
              </Typography>
            </Box>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "28px", sm: "38px", md: "50px" },
                fontWeight: 900,
                color: "#1b1f33",
                lineHeight: 1.15,
                mb: 2,
                letterSpacing: "-0.5px",
                animation: `${fadeUp} 0.6s ease-out 0.1s both`,
              }}
            >
              Our Industry{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Approach
              </Box>
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "16px", sm: "18px" },
                color: "#4c5472",
                maxWidth: "540px",
                mx: "auto",
                lineHeight: 1.8,
                animation: `${fadeUp} 0.6s ease-out 0.2s both`,
              }}
            >
              A structured, proven methodology that turns complex industry challenges
              into elegant, scalable solutions.
            </Typography>
          </Box>

          {/* Approach Cards */}
          <Grid container spacing={{ xs: 2.5, sm: 3 }} alignItems="stretch">
            {approachData.map((item) => (
              <Grid item xs={12} sm={6} md={3} key={item.step}>
                <Box
                  sx={{
                    background: "#ffffff",
                    borderRadius: "24px",
                    p: { xs: 3.5, sm: 4 },
                    height: "100%",
                    boxSizing: "border-box",
                    position: "relative",
                    overflow: "hidden",
                    border: "1px solid rgba(91,124,250,0.1)",
                    boxShadow: "0 4px 24px rgba(17,24,39,0.05)",
                    transition: "box-shadow 0.3s ease, border-color 0.3s ease, transform 0.3s ease",
                    "@media (hover: hover)": {
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 28px 72px rgba(91,124,250,0.14)",
                        borderColor: "rgba(91,124,250,0.22)",
                      },
                      "&:hover::before": { opacity: 1 },
                    },
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: "3px",
                      background: item.gradient,
                      opacity: 0,
                      transition: "opacity 0.3s ease",
                    },
                  }}
                >
                  {/* Background step number */}
                  <Typography
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 16,
                      fontSize: "96px",
                      fontWeight: 900,
                      background: item.gradient,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                      lineHeight: 1,
                      opacity: 0.08,
                      userSelect: "none",
                      pointerEvents: "none",
                    }}
                  >
                    {item.step}
                  </Typography>

                  {/* Gradient dot accent */}
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "14px",
                      background: item.gradient,
                      mb: 3,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 8px 20px rgba(91,124,250,0.2)",
                    }}
                  >
                    <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: "14px" }}>
                      {item.step}
                    </Typography>
                  </Box>

                  <Typography
                    sx={{
                      fontSize: { xs: "19px", sm: "20px" },
                      fontWeight: 800,
                      color: "#1f2937",
                      mb: 1.5,
                      letterSpacing: "-0.2px",
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: "14px", sm: "15px" },
                      color: "#6b7280",
                      lineHeight: 1.75,
                    }}
                  >
                    {item.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ─── Section 4: CTA ─── */}
      <Box
        sx={{
          py: { xs: 10, sm: 12, md: 18 },
          background: "linear-gradient(135deg, #3b4fd4 0%, #5b7cfa 35%, #764ba2 70%, #9333ea 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background glow */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: 400, md: 800 },
            height: { xs: 400, md: 800 },
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(91,124,250,0.14) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        {/* Floating rings */}
        <Box
          sx={{
            position: "absolute",
            top: "20%",
            right: { xs: "2%", md: "8%" },
            width: { xs: 120, md: 200 },
            height: { xs: 120, md: 200 },
            borderRadius: "50%",
            border: "1px dashed rgba(91,124,250,0.2)",
            animation: `${floatBounce} 9s ease-in-out infinite`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "15%",
            left: { xs: "2%", md: "6%" },
            width: { xs: 80, md: 140 },
            height: { xs: 80, md: 140 },
            borderRadius: "50%",
            border: "1px dashed rgba(118,75,162,0.2)",
            animation: `${floatBounce} 11s ease-in-out infinite 2s`,
          }}
        />
        {/* Spinning outer ring */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: { xs: 500, md: 1000 },
            height: { xs: 500, md: 1000 },
            borderRadius: "50%",
            border: "1px dashed rgba(255,255,255,0.04)",
            animation: `${spinSlow} 80s linear infinite`,
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ textAlign: "center" }}>
            {/* Label */}
            <Box
              sx={{
                display: "inline-block",
                px: 2.5,
                py: 0.8,
                mb: 4,
                borderRadius: "50px",
                background: "rgba(255,255,255,0.15)",
                border: "1px solid rgba(255,255,255,0.3)",
                animation: `${fadeUp} 0.6s ease-out both`,
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "11px", sm: "12px" },
                  fontWeight: 700,
                  color: "rgba(255,255,255,0.9)",
                  letterSpacing: "3px",
                  textTransform: "uppercase",
                }}
              >
                Ready to Transform?
              </Typography>
            </Box>

            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "30px", sm: "44px", md: "60px" },
                fontWeight: 900,
                color: "#ffffff",
                lineHeight: 1.1,
                mb: 3,
                letterSpacing: "-1px",
                animation: `${fadeUp} 0.6s ease-out 0.1s both`,
              }}
            >
              Let's Build Something{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.75) 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Remarkable
              </Box>
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: "16px", sm: "19px" },
                color: "rgba(255,255,255,0.6)",
                lineHeight: 1.9,
                maxWidth: "600px",
                mx: "auto",
                mb: 6,
                animation: `${fadeUp} 0.6s ease-out 0.2s both`,
              }}
            >
              Partner with Briozon to bring cutting-edge technology into your industry.
              From strategy to execution — we deliver outcomes that scale and last.
            </Typography>

            {/* CTAs */}
            <Box
              sx={{
                display: "flex",
                gap: { xs: 2, sm: 3 },
                justifyContent: "center",
                flexWrap: "wrap",
                animation: `${fadeUp} 0.6s ease-out 0.3s both`,
              }}
            >
              <Box
                onClick={() => navigate("/contact")}
                sx={{
                  px: { xs: 4, sm: 5 },
                  py: { xs: 1.6, sm: 1.9 },
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  color: "#fff",
                  borderRadius: "50px",
                  fontSize: { xs: "15px", sm: "17px" },
                  fontWeight: 700,
                  cursor: "pointer",
                  boxShadow: "0 8px 36px rgba(91,124,250,0.45)",
                  transition: "all 0.35s ease",
                  letterSpacing: "0.3px",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 20px 56px rgba(91,124,250,0.55)",
                  },
                }}
              >
                Start a Conversation →
              </Box>
              <Box
                onClick={() => navigate("/services")}
                sx={{
                  px: { xs: 4, sm: 5 },
                  py: { xs: 1.6, sm: 1.9 },
                  background: "transparent",
                  color: "rgba(255,255,255,0.85)",
                  borderRadius: "50px",
                  fontSize: { xs: "15px", sm: "17px" },
                  fontWeight: 600,
                  cursor: "pointer",
                  border: "1.5px solid rgba(255,255,255,0.22)",
                  transition: "all 0.35s ease",
                  backdropFilter: "blur(8px)",
                  "&:hover": {
                    background: "rgba(255,255,255,0.08)",
                    borderColor: "rgba(255,255,255,0.5)",
                    transform: "translateY(-5px)",
                    color: "#fff",
                  },
                }}
              >
                Explore Our Services
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default IndustriesIndex;
